'use client';

import { type ForwardRefExoticComponent, Fragment, forwardRef, useMemo } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { TagIcon } from './tag-icon';
import { TagLabel } from './tag-label';
import { TagRoot } from './tag-root';
import type { TagPropsInterface } from './types/tag.types';

/**
 * @since 8.6.0
 */
export const Tag = forwardRef<HTMLSpanElement, TagPropsInterface>(
  (props: TagPropsInterface, forwardedRef) => {
    const { asChild = false, icon, size = 'md', tone = 'gray', children, ...rest } = props;

    // Check if tone is a status and force to show icon when true
    const isStatusTone = useMemo(
      () => ['critical', 'info', 'success', 'warning'].some((status) => status === tone),
      [tone],
    );

    const content = useDecoratedSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          {(icon || isStatusTone) && size === 'md' ? <Tag.Icon>{icon}</Tag.Icon> : null}
          <Tag.Label>{childContent}</Tag.Label>
        </Fragment>
      ),
      dependencies: [icon, isStatusTone, size],
    });

    return (
      <Tag.Root ref={forwardedRef} asChild={asChild} size={size} tone={tone} {...rest}>
        {content}
      </Tag.Root>
    );
  },
) as ForwardRefExoticComponent<TagPropsInterface> & {
  Root: typeof TagRoot;
  Label: typeof TagLabel;
  Icon: typeof TagIcon;
};

Tag.displayName = 'Tag';

Tag.Root = TagRoot;
Tag.Label = TagLabel;
Tag.Icon = TagIcon;
